import { basketLocationToInputFormat } from '@/components/page-specific/catering/common/helpers/addresses'
import {
  GmOrderType,
  GmServiceFormatInput,
  useCreateBasketMutation as useGeneratedMutation,
} from '@/generated/graphql'
import { getTenantFromWindow } from '@/helpers/tenants'
import { useUtmData } from '@/hooks/useUtmData'

import { format } from 'date-fns'

import { ValidUserBasketSettings } from '../../../common/domain/user-basket-settings'
import { MenuVendor } from '../../domain/menu-vendor'
import type { AddItemPayload } from './add-item-mutation'

export const useCreateBasketMutation = () => {
  const [run, result] = useGeneratedMutation()
  const utmData = useUtmData()

  const error =
    result.error ||
    (result.called && !result.loading && !result.data?.basket ? 'Unknown error' : null)

  const createBasket = async ({
    vendor,
    vendorLocationId,
    availabilityId,
    itemPayload,
    account,
    userBasketSettings,
    isCateringTeamOrder,
  }: {
    vendor: Pick<MenuVendor, 'id' | 'companyName' | 'isPantry'>
    vendorLocationId: string | null
    availabilityId: string
    itemPayload?: AddItemPayload
    account: { id: string; name: string } | null
    userBasketSettings: ValidUserBasketSettings
    isCateringTeamOrder?: boolean
  }) => {
    const result = await run({
      variables: {
        availabilityId,
        basketPayload: {
          items: itemPayload
            ? [
                {
                  acknowledgedAt: new Date(),
                  item: itemPayload.item?._id ?? '',
                  note: itemPayload.note,
                  options: (itemPayload.options || []).map(({ option, optionItem, qty }) => ({
                    categoryId: option._id,
                    item: optionItem.item._id,
                    optionItemId: optionItem._id,
                    qty,
                  })),
                  qty: itemPayload.qty,
                },
              ]
            : [],
          settings: {
            ...(account?.id && {
              account: {
                id: Number(account.id),
                name: account.name,
                locationId:
                  userBasketSettings.location.__typename === 'AccountLocation'
                    ? Number(userBasketSettings.location.id)
                    : null,
              },
            }),
            location: basketLocationToInputFormat(userBasketSettings.location),
            billingAddressSameAsDelivery: true,
            deliveryDate: format(new Date(userBasketSettings.date), 'yyyy-MM-dd'),
            deliveryTimeSlot: userBasketSettings.time,
            headCount: userBasketSettings.headCount,
            vendor: {
              id: Number(vendor.id),
              name: vendor.companyName,
              locationId: Number(vendorLocationId),
              url: userBasketSettings.vendor.url,
              imageUrl: userBasketSettings.vendor.imageUrl,
            },
            tenantId: getTenantFromWindow()?.id,
            orderType: vendor.isPantry ? GmOrderType.PantryFill : undefined,
          },
          ...(isCateringTeamOrder && { isCateringTeamOrder: true }),
          options: {
            format: GmServiceFormatInput.Individual,
            rep: false,
            isHighRisk: false,
            addons: {
              napkins: false,
              plates: false,
              service: false,
            },
            notes: {
              cart: '',
              account: '',
              vendor: '',
            },
          },
          utmData,
        },
      },
    })
    return result
  }

  return {
    run: createBasket,
    loading: result.loading,
    basket: result.data?.basket,
    error,
  }
}
